import React, { useEffect } from "react";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import styled from "@emotion/styled";
import {
  Alert as MuiAlert,
  Checkbox,
  FormControlLabel,
  Button,
  MenuItem,
  TextField as MuiTextField,
  FormHelperText,
  ListSubheader,
  RadioGroup,
  Box,
  Typography,
  FormLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { spacing } from "@mui/system";
import RadioBtnStyled from "components/custom/RadioBtnStyled";
import useNotification from "hooks/useNotification";
import useReferentiel from "hooks/useReferentiel";
import useProgram from "hooks/useProgram";
import usePublishedProg from "hooks/usePublishedProg";
import useDataAppcode from "hooks/useDataAppcode";
import CodeGen from "components/CodeGen";
import useDataUser from "hooks/useDataUser";
import { fDateTime } from "utils/formatTime";
// ----------------------------------------------------------------------
const USER_MODES = [
  {
    code: "standard",
    label: "Standard",
  },
  {
    code: "demo",
    label: "Démo",
  },
  {
    code: "test",
    label: "Test",
  },
];
const TextField = styled(MuiTextField)(spacing);
export default function AppcodeForm({ editData, onComplete }) {
  const { publishedPrograms, getPublishedPrograms } = usePublishedProg();
  const { saveAppcode, initAppcodes } = useDataAppcode();
  const { users } = useDataUser();
  const { notif } = useNotification();
  const { refs } = useReferentiel();
  useEffect(() => {
    getPublishedPrograms();
  }, []);
  const today = new Date(Date.now() - 86400000);
  const dataSchema = Yup.object().shape({
    code: Yup.string().max(255).required("Veuillez renseigner le code"),
    description: Yup.string().required(`Veuillez renseigner la description`),
    refstatus: Yup.string().max(255).required("Veuillez renseigner le statut"),
    userapp: Yup.string()
      .max(255)
      .required("Veuillez renseigner l'utilisateur d'application"),
    userMode: Yup.string()
      .max(50)
      .required("Veuillez renseigner le mode utilisé"),
    userprogram:
      editData && editData.userprogram
        ? Yup.object()
        : Yup.string().max(255).required("Veuillez renseigner le programme"),
    enabledat: Yup.date().required(`Veuillez renseigner la date d'activation`),
    isEmailSended: Yup.boolean(),
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: editData ? editData.code : "",
      description: editData ? editData.description : "",
      refstatus: editData && editData.refstatus ? editData.refstatus.id : "",
      userapp: editData && editData.userapp ? editData.userapp.id : "",
      userMode:
        editData && editData.userMode ? editData.userMode : USER_MODES[0].code,
      userprogram: editData && editData.userprogram ? editData.userprogram : "",
      isEmailSended: editData ? Boolean(editData.isEmailSended) : true,
    },
    validationSchema: dataSchema,
    onSubmit: async () => {
      try {
        let newData = {};
        if (editData && editData.id) {
          newData = { ...editData, ...values };
          newData.userprogram = newData.userprogram.id;
        } else {
          newData = { ...values };
        }
        await saveAppcode(newData);
        await initAppcodes();
        notif("Sauvegarde réussie");
        onComplete();
      } catch (error) {
        console.error(error);
        notif(null, error);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    handleChange,
    setFieldValue,
  } = formik;
  useEffect(() => {
    if (!setFieldValue) return;
    if (editData && editData.enabledat) {
      setFieldValue("enabledat", editData.enabledat);
    } else {
      setFieldValue("enabledat", new Date());
    }
  }, [setFieldValue, editData]);

  const refstatus = Array.isArray(refs.refstatus) ? refs.refstatus : [];
  if (!users)
    return (
      <>
        Aucun utilisateur d'application n'est trouvé. Veuillez créer au moins un
        utilisateur d'application avant la création d'un code d'invitation
      </>
    );
  if (!publishedPrograms || publishedPrograms.length < 1)
    return (
      <>
        Aucun programme publié n'est trouvé. Veuillez créer au moins un avant la
        création d'un code d'invitation
      </>
    );
  console.log(errors);
  return (
    <>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          {editData && editData.code ? (
            <>
              <TextField
                fullWidth
                type="text"
                label="Code"
                value={editData.code}
                disabled
                my={2}
              />
            </>
          ) : (
            <>
              <CodeGen
                name="code"
                label="Code d'application"
                title="Générer le code"
                placeholder="Code d'application"
                value={values.code}
                onChange={(val) => {
                  setFieldValue("code", val);
                }}
                error={Boolean(touched.code && errors.v)}
              />

              {errors.code && (
                <FormHelperText error>{errors.code} </FormHelperText>
              )}
            </>
          )}

          <TextField
            fullWidth
            type="text"
            name="description"
            label="Description"
            {...getFieldProps("description")}
            error={Boolean(touched.description && errors.description)}
            helperText={touched.description && errors.description}
            my={2}
          />

          <TextField
            fullWidth
            select
            label="Statut"
            value={values.refstatus}
            onChange={handleChange("refstatus")}
            {...getFieldProps("refstatus")}
            error={Boolean(touched.refstatus && errors.refstatus)}
            helperText={touched.refstatus && errors.refstatus}
            my={2}
          >
            {refstatus.map((option, i) => (
              <MenuItem key={i} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="Utilisateur"
            value={values.userapp}
            onChange={handleChange("userapp")}
            {...getFieldProps("userapp")}
            error={Boolean(touched.userapp && errors.userapp)}
            helperText={touched.userapp && errors.userapp}
            my={2}
          >
            {users.map((option, i) => (
              <MenuItem key={i} value={option.id}>
                {`${option.pseudo ? option.pseudo : ""}-${
                  option.firstname ? option.firstname : ""
                } ${option.lastname ? option.lastname : ""}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="Mode"
            value={values.userMode}
            onChange={handleChange("userMode")}
            {...getFieldProps("userMode")}
            error={Boolean(touched.userMode && errors.userMode)}
            helperText={touched.userMode && errors.userMode}
            my={2}
          >
            {USER_MODES.map((option, i) => (
              <MenuItem key={i} value={option.code}>
                {`${option.label}`}
              </MenuItem>
            ))}
          </TextField>
          {editData && editData.id && editData.userprogram ? (
            <>
              <TextField
                fullWidth
                type="text"
                label="Programme affecté"
                value={editData.userprogram.title}
                disabled
                my={2}
              />
            </>
          ) : (
            <>
              <TextField
                fullWidth
                select
                label="Programme affecté"
                value={values.userprogram}
                onChange={handleChange("userprogram")}
                {...getFieldProps("userprogram")}
                error={Boolean(touched.userprogram && errors.userprogram)}
                helperText={touched.userprogram && errors.userprogram}
                my={2}
              >
                {publishedPrograms.map((option, i) => (
                  <MenuItem key={i} value={option.id}>
                    {`${option.title}`}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
          {editData && editData.code ? (
            <></>
          ) : (
            <DatePicker
              inputFormat="dd/MM/yyyy"
              onChange={(value) => {
                setFieldValue("enabledat", value, true);
              }}
              value={values.enabledat}
              renderInput={(params) => (
                <TextField
                  fullWidth
                  label="Date d'activation"
                  {...getFieldProps("enabledat")}
                  error={Boolean(touched.enabledat && errors.enabledat)}
                  helperText={touched.enabledat && errors.enabledat}
                  my={2}
                  {...params}
                />
              )}
            />
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            my={2}
          >
            <FormLabel component="legend">
              Envoyé un email d'invitation
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={values.isEmailSended}
              onChange={handleChange("isEmailSended")}
            >
              <FormControlLabel
                value={Boolean(true)}
                control={<RadioBtnStyled />}
                label={<Typography>Oui</Typography>}
              />
              <FormControlLabel
                value={Boolean(false)}
                control={<RadioBtnStyled />}
                label={<Typography>Non</Typography>}
              />
            </RadioGroup>
          </Box>
          {editData && editData.code && (
            <>
              <TextField
                fullWidth
                type="text"
                label="Terminal utilisé"
                value={editData.device}
                disabled
                my={2}
              />

              <TextField
                fullWidth
                type="text"
                label="Date d'activation"
                value={fDateTime(editData.enabledat)}
                disabled
                my={2}
              />
              {editData.lastusedat && (
                <TextField
                  fullWidth
                  type="text"
                  label="Date de désactivation"
                  value={fDateTime(editData.lastusedat)}
                  disabled
                  my={2}
                />
              )}
              <TextField
                fullWidth
                type="text"
                label="Créé par"
                value={editData.createdby?.firstname}
                disabled
                my={2}
              />
              <TextField
                fullWidth
                type="text"
                label="Mis à jour par"
                value={editData.modifiedby?.lastname}
                disabled
                my={2}
              />
            </>
          )}
          <Button
            sx={{ my: 2 }}
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            Valider
          </Button>
        </Form>
      </FormikProvider>
    </>
  );
}
